import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import Modal from "../Modal/Modal";
import FileListElement from "../FileListElement/FileListElement";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./AttachFileDialog.module.css";

type AttachFileDialogProps = {
  existingFiles?: File[];
  onDismiss: () => void;
  onSubmit: (selected: File[]) => void;
  accept?: string;
  maxFiles?: number;
};

export default function AttachFileDialog({
  existingFiles = [],
  onDismiss,
  onSubmit,
  accept,
  maxFiles,
}: AttachFileDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  const [files, setFiles] = useState<File[]>(existingFiles);

  useEffect(() => {
    // Update local state only when actual file references change
    setFiles((prev: File[]): File[] => {
      const sameLength: boolean = prev.length === existingFiles.length;
      const sameRefs: boolean =
        sameLength &&
        prev.every((p: File, i: number): boolean => p === existingFiles[i]);
      return sameRefs ? prev : existingFiles;
    });
  }, [existingFiles]);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const matchesAccept = useCallback(
    (file: File): boolean => {
      if (!accept) return true;
      const tokens = accept.split(",").map((t) => t.trim().toLowerCase());
      const name = file.name.toLowerCase();
      const type = (file.type || "").toLowerCase();
      return tokens.some((t) => {
        if (!t) return false;
        if (t.startsWith(".")) return name.endsWith(t);
        if (t.endsWith("/*")) return type.startsWith(t.slice(0, -1));
        return type === t;
      });
    },
    [accept]
  );

  const appendFiles = useCallback(
    (incoming: FileList) => {
      setFiles((current) => {
        const filtered = Array.from(incoming).filter(matchesAccept);
        const merged = [...current, ...filtered];
        if (typeof maxFiles === "number" && merged.length > maxFiles) {
          return merged.slice(merged.length - maxFiles);
        }
        return merged;
      });
    },
    [matchesAccept, maxFiles]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      appendFiles(files);
    }
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        appendFiles(files);
      }
    },
    [appendFiles]
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const removeFile = (name: string) => {
    setFiles((files) => files.filter((f) => f.name !== name));
  };

  return (
    <Modal visible={true} onDismiss={onDismiss}>
      <div className={styles.dialog}>
        <h4>Attach File</h4>
        <div className={styles["attach-file-container"]}>
          <div
            className={
              styles["drag-and-drop-container"] +
              (isDragging ? ` ${styles.dragging}` : "")
            }
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
          >
            <Icon name="Upload" size="2rem" />
            {isDragging ? (
              <p>Release to drop</p>
            ) : (
              <>
                <p>
                  Drag and Drop here <br /> or
                </p>
                <Button
                  variant="secondary"
                  text="Browse"
                  icon="Image"
                  size="small"
                  onClick={handleClick}
                />
              </>
            )}
            <input
              type="file"
              multiple={maxFiles !== 1}
              accept={accept}
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
          {files.length > 0 && (
            <div className={styles["file-list"]}>
              {files.map((f) => (
                <FileListElement
                  key={`${f.name}-${f.size}`}
                  file={f}
                  onRemoveClicked={removeFile}
                />
              ))}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <Button
            variant="transparent"
            size="small"
            text="Close"
            onClick={() => onDismiss()}
          />
          <Button
            variant="secondary"
            icon="Check_mark"
            size="small"
            text="Save"
            onClick={() => onSubmit(files)}
          />
        </div>
      </div>
    </Modal>
  );
}
